import type { QueryClient } from "@tanstack/react-query"
import { createFileRoute, stripSearchParams } from "@tanstack/react-router"
import { useCallback, useMemo } from "react"
import { ETSPage } from "@/components/pages/ets/page"
import {
	dailyVisitorsOptions,
	monthlyVisitorsOptions,
	topCustomersOptions,
	topPagesOptions,
	usageSummaryOptions,
} from "@/hooks/queries/analytics"
import {
	euetsUsersOptions,
	voyageActionStatsOptions,
	voyageActionsOptions,
} from "@/hooks/queries/euets"
import {
	registryAccountCountriesOptions,
	registryAccountStatsOptions,
} from "@/hooks/queries/registries"
import { useTabNavigation } from "@/hooks/use-tab-navigation"
import { DEFAULT_PAGE_SIZE } from "@/lib/constants"
import { type EtsTab, etsSearchSchema } from "@/lib/schemas/ets"
import { visitorChartMonths } from "@/lib/usage"

type TabPrefetcher = (qc: QueryClient, days: number) => void

const TAB_PREFETCHERS: Record<EtsTab, TabPrefetcher> = {
	users: (qc) => {
		qc.prefetchQuery(
			euetsUsersOptions({
				page: 1,
				pageSize: DEFAULT_PAGE_SIZE,
				sortDesc: true,
			}),
		)
	},
	"voyage-actions": (qc) => {
		qc.prefetchQuery(voyageActionsOptions())
		qc.prefetchQuery(voyageActionStatsOptions())
	},
	registries: (qc) => {
		qc.prefetchQuery(registryAccountStatsOptions())
		qc.prefetchQuery(registryAccountCountriesOptions())
	},
	usage: (qc, days) => {
		const months = visitorChartMonths(days)
		if (months) {
			qc.prefetchQuery(monthlyVisitorsOptions("euets", months))
		} else {
			qc.prefetchQuery(dailyVisitorsOptions("euets", days))
		}
		qc.prefetchQuery(usageSummaryOptions("euets", days))
		qc.prefetchQuery(topCustomersOptions("euets", days))
		qc.prefetchQuery(topPagesOptions("euets", days))
	},
}

export const Route = createFileRoute("/_authenticated/ets/")({
	validateSearch: etsSearchSchema,
	search: {
		middlewares: [stripSearchParams(etsSearchSchema.parse({}))],
	},
	loaderDeps: ({ search }) => ({ tab: search.tab, days: search.days }),
	loader: ({ context, deps }) => {
		TAB_PREFETCHERS[deps.tab ?? "usage"](context.queryClient, deps.days)
	},
	component: ETSRoute,
})

function ETSRoute() {
	const { tab: tabSearch, days } = Route.useSearch()
	const navigate = Route.useNavigate()
	const keptSearch = useMemo(() => ({ days }), [days])
	const { tab, onTabChange } = useTabNavigation(
		navigate,
		"usage",
		tabSearch,
		keptSearch,
	)
	const onDaysChange = useCallback(
		(value: number) => {
			const period = etsSearchSchema.shape.days.parse(value)
			navigate({ search: (prev) => ({ ...prev, days: period }), replace: true })
		},
		[navigate],
	)
	return (
		<ETSPage
			tab={tab}
			onTabChange={onTabChange}
			days={days}
			onDaysChange={onDaysChange}
		/>
	)
}
